import messaging from '@react-native-firebase/messaging';
import firebase from '@react-native-firebase/app';
import {Platform} from 'react-native';
import {API_CONFIG} from '../config/api';
import {getNotificationPreference} from './notificationPreferences';

class NotificationService {
  private token: string | null = null;

  /**
   * Initialize push notifications
   * Call this on app startup
   */
  async initialize(): Promise<string | null> {
    try {
      // Log Firebase project info for debugging
      const app = firebase.app();
      const options = app.options;
      console.log('Firebase Project ID:', options.projectId);
      console.log('Firebase Sender ID:', options.messagingSenderId);
      console.log('Firebase App ID:', options.appId);

      // On iOS, request Firebase-level permission. On Android, skip — handled in App.tsx via PermissionsAndroid
      if (Platform.OS === 'ios') {
        const authStatus = await messaging().requestPermission();
        const enabled =
          authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
          authStatus === messaging.AuthorizationStatus.PROVISIONAL;

        if (!enabled) {
          console.log('Push notification permission denied');
          return null;
        }
      }

      // Ensure device is registered for remote messages
      const isRegistered = messaging().isDeviceRegisteredForRemoteMessages;
      console.log('Device registered for remote messages:', isRegistered);
      if (!isRegistered) {
        await messaging().registerDeviceForRemoteMessages();
        console.log('Device now registered for remote messages');
      }

      // Get FCM token
      const token = await messaging().getToken();
      this.token = token;
      console.log('FCM Token:', token);

      // Register with backend
      if (token) {
        await this.registerWithBackend();
      } else {
        console.log('FCM token is null/empty — cannot register');
      }

      // Listen for token refresh
      messaging().onTokenRefresh(async (newToken) => {
        console.log('FCM Token refreshed:', newToken);
        this.token = newToken;
        await this.registerWithBackend();
      });

      return token;
    } catch (error) {
      console.log('Error initializing push notifications:', error);
      return null;
    }
  }

  /**
   * Register device token with Laravel backend
   */
  async registerWithBackend(): Promise<boolean> {
    if (!this.token) return false;

    try {
      // Get user's notification preference
      const notificationMode = await getNotificationPreference();
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/app/notifications/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: this.token,
          platform: Platform.OS,
          notification_mode: notificationMode,
        }),
      });

      const data = await response.json();
      console.log('Device registered for push:', data);
      return data.success;
    } catch (error) {
      console.log('Error registering device:', error);
      return false;
    }
  }

  /**
   * Set up notification listeners
   */
  setupListeners(
    onNotificationReceived?: (message: any) => void,
    onNotificationOpened?: (message: any) => void
  ) {
    try {
      // Foreground notifications
      messaging().onMessage(async (remoteMessage) => {
        console.log('Notification received in foreground:', remoteMessage);
        onNotificationReceived?.(remoteMessage);
      });

      // Background/quit notification opened (app was in background, not killed)
      messaging().onNotificationOpenedApp((remoteMessage) => {
        console.log('Notification opened app from background:', remoteMessage);
        onNotificationOpened?.(remoteMessage);
      });

      // Note: getInitialNotification() for cold start is handled in App.tsx
      // to ensure it's captured before the splash screen finishes.
    } catch (error) {
      console.log('Error setting up notification listeners:', error);
    }
  }

  /**
   * Unregister device from push notifications
   */
  async unregister(): Promise<boolean> {
    if (!this.token) return false;

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/app/notifications/unregister`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: this.token,
        }),
      });

      // Delete FCM token
      await messaging().deleteToken();

      const data = await response.json();
      console.log('Device unregistered:', data);
      this.token = null;
      return data.success;
    } catch (error) {
      console.log('Error unregistering device:', error);
      return false;
    }
  }

  /**
   * Get current push token
   */
  getToken(): string | null {
    return this.token;
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
